import { useCallback } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import isURLLike from './is-url-like';
import normalizeUrl from './normalize-url';
import { CREATE_TYPE } from './constants';
import { store as blockEditorStore } from '../../store';
import type {
	FetchLinkSuggestions,
	FetchLinkSuggestionsHandler,
	LinkControlSuggestion,
	LinkSuggestionsQuery,
	TransformSuggestions,
} from './types';

export const handleNoop = (): Promise< LinkControlSuggestion[] > =>
	Promise.resolve( [] );

export const handleDirectEntry = (
	val: string
): Promise< LinkControlSuggestion[] > => {
	const { url, type } = normalizeUrl( val );

	return Promise.resolve( [
		{
			id: val,
			title: val,
			url,
			type,
		},
	] );
};

const handleEntitySearch = async (
	val: string,
	suggestionsQuery: LinkSuggestionsQuery,
	fetchSearchSuggestions: FetchLinkSuggestions | undefined,
	withCreateSuggestion: boolean | undefined,
	pageOnFront: number | undefined,
	pageForPosts: number | undefined,
	transformSuggestions: TransformSuggestions | null | undefined
): Promise< LinkControlSuggestion[] > => {
	const { isInitialSuggestions } = suggestionsQuery;

	// Entity search needs the editor's suggestions fetcher.
	let results = await fetchSearchSuggestions!( val, suggestionsQuery );

	if ( transformSuggestions ) {
		results = await transformSuggestions( results, {
			isInitialSuggestions: !! isInitialSuggestions,
			searchTerm: val,
		} );
	}

	// Identify front page and update type to match. Posts, terms and media can
	// share an id, so only pages are considered.
	results.map( ( result ) => {
		if ( result.type !== 'page' ) {
			return result;
		}

		if ( Number( result.id ) === pageOnFront ) {
			result.isFrontPage = true;
			return result;
		} else if ( Number( result.id ) === pageForPosts ) {
			result.isBlogHome = true;
			return result;
		}

		return result;
	} );

	// If displaying initial suggestions just return plain results.
	if ( isInitialSuggestions ) {
		return results;
	}

	// Here we append a faux suggestion to represent a "CREATE" option. This
	// is detected in the rendering of the search results and handled as a
	// special case. This is currently necessary because the suggestions
	// dropdown will only appear if there are valid suggestions and
	// therefore unless the create option is a suggestion it will not
	// display in scenarios where there are no results returned from the
	// API. In addition promoting CREATE to a first class suggestion affords
	// the a11y benefits afforded by `URLInput` to all suggestions (eg:
	// keyboard handling, ARIA roles...etc).
	//
	// Note also that the value of the `title` and `url` properties must correspond
	// to the text value of the `<input>`. This is because `title` is used
	// when creating the suggestion. Similarly `url` is used when using keyboard to select
	// the suggestion (the <form> `onSubmit` handler falls-back to `url`).
	return isURLLike( val ) || ! withCreateSuggestion
		? results
		: results.concat( {
				// the `id` prop is intentionally omitted here because it
				// is never exposed as part of the component's public API.
				// see: https://github.com/WordPress/gutenberg/pull/19775#discussion_r378931316.
				title: val, // Must match the existing `<input>`s text value.
				url: val, // Must match the existing `<input>`s text value.
				type: CREATE_TYPE,
			} );
};

export default function useSearchHandler(
	suggestionsQuery: LinkSuggestionsQuery,
	allowDirectEntry: boolean,
	withCreateSuggestion: boolean | undefined,
	transformSuggestions: TransformSuggestions | null | undefined
) {
	const { fetchSearchSuggestions, pageOnFront, pageForPosts } = useSelect(
		( select ) => {
			const { getSettings } = select( blockEditorStore );

			return {
				pageOnFront: getSettings().pageOnFront,
				pageForPosts: getSettings().pageForPosts,
				fetchSearchSuggestions:
					getSettings().__experimentalFetchLinkSuggestions,
			};
		},
		[]
	);

	const directEntryHandler: FetchLinkSuggestionsHandler = allowDirectEntry
		? handleDirectEntry
		: handleNoop;

	return useCallback(
		(
			val: string,
			{ isInitialSuggestions }: { isInitialSuggestions?: boolean }
		) => {
			return isURLLike( val )
				? directEntryHandler( val, { isInitialSuggestions } )
				: handleEntitySearch(
						val,
						{ ...suggestionsQuery, isInitialSuggestions },
						fetchSearchSuggestions,
						withCreateSuggestion,
						pageOnFront,
						pageForPosts,
						transformSuggestions
					);
		},
		[
			directEntryHandler,
			fetchSearchSuggestions,
			pageOnFront,
			pageForPosts,
			suggestionsQuery,
			transformSuggestions,
			withCreateSuggestion,
		]
	);
}
